import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { ACCOUNTS_PAYABLE_QUEUE_TYPES } from "../constants";

const initiallyExpanded = [
  ACCOUNTS_PAYABLE_QUEUE_TYPES.AWAITING_ACTION,
  ACCOUNTS_PAYABLE_QUEUE_TYPES.ON_USER_HOLD,
  ACCOUNTS_PAYABLE_QUEUE_TYPES.HISTORICAL,
  ACCOUNTS_PAYABLE_QUEUE_TYPES.SEARCH,
];

const initialState = Object.fromEntries(
  Object.values(ACCOUNTS_PAYABLE_QUEUE_TYPES).map((queue) => [
    queue,
    {
      expanded: initiallyExpanded.includes(queue),
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
      // Initially sorting by date_appeared as a special request from AP
      sorting: [{ id: "date_appeared", desc: false }],
      density: "comfortable",
      columnVisibility: {},
      columnPinning: { left: [], right: [] },
      globalFilter: "",
    },
  ])
);

/**
 * Zustand store to manage and persist the state of request queues.
 * Includes state for pagination, sorting, density, column visibility, and column pinning.
 *
 * @returns {Object} The store object with state and actions to manage request queues.
 */
export const useRequestsQueueStatePersistedStore = create()(
  persist(
    devtools((set) => ({
      /**
       * The state object containing the state of all request queues.
       * @type {Object}
       */
      queuesState: initialState,
      /**
       * Updates the state of a specific request queue.
       *
       * @param {Object} params - The parameters to update the state.
       * @param {string} params.queue - The queue identifier.
       * @param {Object} params.pagination - The pagination state.
       * @param {number} params.pagination.pageIndex - The current page index.
       * @param {number} params.pagination.pageSize - The number of items per page.
       * @param {Array} params.sorting - The sorting state.
       * @param {string} params.density - The density setting ('comfortable' | 'compact' | 'spacious').
       * @param {Object} params.columnVisibility - The visibility state of columns.
       * @param {Object} params.columnPinning - The pinning state of columns.
       */
      setQueueState: (queue, newState) =>
        set((state) => ({
          queuesState: {
            ...state.queuesState,
            [queue]: {
              ...state.queuesState[queue],
              ...newState,
            },
          },
        })),
      /**
       * Sets the expanded state of queue accordions.
       *
       * @param {Array} queueAccordions - The array of queue accordions.
       * @param {string} queueKey - The key of the queue to expand.
       * @param {boolean} expanded - The expanded state.
       */
      setExpanded: (queueAccordions, queueKey, expanded) =>
        set((state) => ({
          queuesState: {
            ...state.queuesState,
            ...Object.fromEntries(
              Object.values(queueAccordions).map((accordion) => [
                accordion.key,
                { ...state.queuesState[accordion.key], expanded: false },
              ])
            ),
            [queueKey]: { ...state.queuesState[queueKey], expanded },
          },
        })),
    })),
    {
      name: "request-queue-store",
    }
  )
);
